/**
 * TimeMotionSettings - Configuration panel for Time Motion navigation
 * Allows selecting step unit (second, minute, hour) and step value
 * With a button to open the navigation overlay
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  HStack,
  Text,
  Badge,
  Wrap,
  WrapItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  useDisclosure,
} from '@chakra-ui/react';
import { ExpandableLabel } from '../ExpandableLabel';
import { MotionOpenButton } from './MotionOpenButton';
import { globalEventStream, createEvent } from '../../universal_architecture/core/EventStream';
import type { BaseEvent, EventSubscription } from '../../universal_architecture/types/EventTypes';
import type { TimeStepUnit } from '../../../../../types/MotionTypes';
import { getLocalStorageItem, setLocalStorageItem } from '../../../../../utils/localStorageUtils';

// Step unit options
const STEP_UNITS: { value: TimeStepUnit; label: string }[] = [
  { value: 'second', label: 'sec' },
  { value: 'minute', label: 'min' },
  { value: 'hour', label: 'hour' },
];

// Step values per unit
const STEP_VALUES: Record<TimeStepUnit, number[]> = {
  second: [1, 5, 10, 30],
  minute: [1, 5, 15, 30],
  hour: [1, 6, 12, 24],
};

interface TimeMotionSettingsProps {
  chartId: string;
  disabled?: boolean;
}

/**
 * Format step for display
 */
const formatStep = (unit: TimeStepUnit, value: number): string => {
  const unitLabels: Record<TimeStepUnit, string> = {
    second: 's',
    minute: 'm',
    hour: 'h',
  };
  return `${value}${unitLabels[unit]}`;
};

/**
 * Calculate range description (shows exact time, not rounded)
 */
const formatRange = (unit: TimeStepUnit, value: number, range: number = 300): string => {
  const totalSeconds = value * range * (unit === 'second' ? 1 : unit === 'minute' ? 60 : 3600);

  if (totalSeconds < 60) return `${totalSeconds}s`;
  if (totalSeconds < 3600) return `${Math.floor(totalSeconds / 60)}m`;
  if (totalSeconds < 86400) {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  }
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  if (hours === 0) return `${days}d`;
  return `${days}d ${hours}h`;
};

/**
 * StepUnitSelector - Badge-based selector for step unit
 */
const StepUnitSelector: React.FC<{
  value: TimeStepUnit;
  onChange: (value: TimeStepUnit) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled = false }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleSelect = (unit: TimeStepUnit) => {
    if (disabled) return;
    onChange(unit);
    onClose();
  };

  const handleOpen = () => {
    if (!disabled) onOpen();
  };

  return (
    <Popover isOpen={isOpen} onOpen={handleOpen} onClose={onClose} placement="bottom-start">
      <PopoverTrigger>
        <Box
          as="button"
          display="flex"
          alignItems="center"
          gap={0.5}
          px={1.5}
          py={0.5}
          borderRadius="sm"
          bg="gray.100"
          cursor={disabled ? 'not-allowed' : 'pointer'}
          opacity={disabled ? 0.5 : 1}
          _hover={disabled ? {} : { bg: 'gray.200' }}
          transition="background 0.2s"
        >
          <Text fontSize="xs" fontWeight="medium" color="gray.700">
            {STEP_UNITS.find(u => u.value === value)?.label}
          </Text>
          <Text fontSize="2xs" color="gray.400">
            ▼
          </Text>
        </Box>
      </PopoverTrigger>

      <PopoverContent width="auto" maxW="150px" _focus={{ boxShadow: 'sm' }}>
        <PopoverBody p={2}>
          <Text fontSize="2xs" color="gray.500" mb={1}>
            Step Unit
          </Text>
          <Wrap spacing={1}>
            {STEP_UNITS.map(unit => (
              <WrapItem key={unit.value}>
                <Badge
                  colorScheme={unit.value === value ? 'purple' : 'gray'}
                  variant={unit.value === value ? 'solid' : 'outline'}
                  fontSize="xs"
                  px={1.5}
                  py={0.5}
                  borderRadius="sm"
                  cursor="pointer"
                  onClick={() => handleSelect(unit.value)}
                  _hover={{
                    bg: unit.value === value ? undefined : 'gray.100',
                    transform: 'scale(1.02)'
                  }}
                  transition="all 0.15s"
                >
                  {unit.label}
                </Badge>
              </WrapItem>
            ))}
          </Wrap>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

/**
 * StepValueSelector - Badge-based selector for step value
 */
const StepValueSelector: React.FC<{
  unit: TimeStepUnit;
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}> = ({ unit, value, onChange, disabled = false }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const values = STEP_VALUES[unit];

  const handleSelect = (val: number) => {
    if (disabled) return;
    onChange(val);
    onClose();
  };

  const handleOpen = () => {
    if (!disabled) onOpen();
  };

  return (
    <Popover isOpen={isOpen} onOpen={handleOpen} onClose={onClose} placement="bottom-start">
      <PopoverTrigger>
        <Box
          as="button"
          display="flex"
          alignItems="center"
          gap={0.5}
          px={1.5}
          py={0.5}
          borderRadius="sm"
          bg="gray.100"
          cursor={disabled ? 'not-allowed' : 'pointer'}
          opacity={disabled ? 0.5 : 1}
          _hover={disabled ? {} : { bg: 'gray.200' }}
          transition="background 0.2s"
        >
          <Text fontSize="xs" fontWeight="medium" color="gray.700">
            {formatStep(unit, value)}
          </Text>
          <Text fontSize="2xs" color="gray.400">
            ▼
          </Text>
        </Box>
      </PopoverTrigger>

      <PopoverContent width="auto" maxW="180px" _focus={{ boxShadow: 'sm' }}>
        <PopoverBody p={2}>
          <Text fontSize="2xs" color="gray.500" mb={1}>
            Step Value
          </Text>
          <Wrap spacing={1}>
            {values.map(val => (
              <WrapItem key={val}>
                <Badge
                  colorScheme={val === value ? 'purple' : 'gray'}
                  variant={val === value ? 'solid' : 'outline'}
                  fontSize="xs"
                  px={1.5}
                  py={0.5}
                  borderRadius="sm"
                  cursor="pointer"
                  onClick={() => handleSelect(val)}
                  _hover={{
                    bg: val === value ? undefined : 'gray.100',
                    transform: 'scale(1.02)'
                  }}
                  transition="all 0.15s"
                >
                  {formatStep(unit, val)}
                </Badge>
              </WrapItem>
            ))}
          </Wrap>
          <Text fontSize="2xs" color="gray.400" mt={2}>
            Range: ±{formatRange(unit, value)}
          </Text>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

/**
 * TimeMotionSettings component
 */
export const TimeMotionSettings: React.FC<TimeMotionSettingsProps> = ({
  chartId,
  disabled = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeMotionType, setActiveMotionType] = useState<'planet-drag' | 'time-motion' | null>(null);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  // Load settings from localStorage
  const getStoredSettings = useCallback(() => {
    const key = `time-motion-settings-${chartId}`;
    return getLocalStorageItem(key, { stepUnit: 'minute' as TimeStepUnit, stepValue: 1 });
  }, [chartId]);

  const [stepUnit, setStepUnit] = useState<TimeStepUnit>(() => getStoredSettings().stepUnit);
  const [stepValue, setStepValue] = useState<number>(() => getStoredSettings().stepValue);

  // Save settings to localStorage
  const saveSettings = useCallback((unit: TimeStepUnit, value: number) => {
    setLocalStorageItem(`time-motion-settings-${chartId}`, { stepUnit: unit, stepValue: value });
  }, [chartId]);

  // Handle step unit change
  const handleUnitChange = useCallback((unit: TimeStepUnit) => {
    setStepUnit(unit);
    // Reset step value to first available if current is not in new unit's list
    const values = STEP_VALUES[unit];
    if (!values.includes(stepValue)) {
      setStepValue(values[0]);
      saveSettings(unit, values[0]);
    } else {
      saveSettings(unit, stepValue);
    }

    // Emit settings changed event
    globalEventStream.emit(createEvent<BaseEvent>(
      'time.motion.settings.changed',
      'TimeMotionSettings',
      {
        metadata: {
          chartId,
          stepUnit: unit,
          stepValue: values.includes(stepValue) ? stepValue : values[0],
        },
      }
    ));
  }, [chartId, stepValue, saveSettings]);

  // Handle step value change
  const handleValueChange = useCallback((value: number) => {
    setStepValue(value);
    saveSettings(stepUnit, value);

    // Emit settings changed event
    globalEventStream.emit(createEvent<BaseEvent>(
      'time.motion.settings.changed',
      'TimeMotionSettings',
      {
        metadata: {
          chartId,
          stepUnit,
          stepValue: value,
        },
      }
    ));
  }, [chartId, stepUnit, saveSettings]);

  // Handle open button click
  const handleOpenClick = useCallback(() => {
    globalEventStream.emit(createEvent<BaseEvent>(
      'time.motion.open',
      'TimeMotionSettings',
      {
        metadata: {
          chartId,
          stepUnit,
          stepValue,
        },
      }
    ));
  }, [chartId, stepUnit, stepValue]);

  // Listen for all motion overlay open/close events
  useEffect(() => {
    const subscriptions: EventSubscription[] = [];

    // Time motion events
    subscriptions.push(
      globalEventStream.subscribe('time.motion.overlay.opened', (event) => {
        if (event.metadata?.chartId === chartId) {
          setActiveMotionType('time-motion');
          setIsOverlayOpen(true);
        }
      })
    );

    subscriptions.push(
      globalEventStream.subscribe('time.motion.overlay.closed', (event) => {
        if (event.metadata?.chartId === chartId) {
          setActiveMotionType(null);
          setIsOverlayOpen(false);
        }
      })
    );

    // Planet drag events
    subscriptions.push(
      globalEventStream.subscribe('planet.drag.overlay.opened', (event) => {
        if (event.metadata?.chartId === chartId) {
          setActiveMotionType('planet-drag');
        }
      })
    );

    subscriptions.push(
      globalEventStream.subscribe('planet.drag.overlay.closed', (event) => {
        if (event.metadata?.chartId === chartId) {
          setActiveMotionType(null);
        }
      })
    );

    // Listen for status response (from active overlays)
    subscriptions.push(
      globalEventStream.subscribe('motion.state.response', (event) => {
        if (event.metadata?.chartId === chartId) {
          const motionType = event.metadata?.motionType as 'planet-drag' | 'time-motion' | null;
          setActiveMotionType(motionType);
          if (motionType === 'time-motion') {
            setIsOverlayOpen(true);
          }
        }
      })
    );

    // Request current state from any active overlay
    globalEventStream.emit(createEvent<BaseEvent>(
      'motion.state.query',
      'TimeMotionSettings',
      { metadata: { chartId } }
    ));

    return () => {
      subscriptions.forEach(sub => sub.unsubscribe());
    };
  }, [chartId]);

  // Toggle expand
  const handleToggle = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  return (
    <div className="planet-detail-expandable-container">
      <div className="planet-detail-row">
        <ExpandableLabel
          label="Movement"
          isExpanded={isExpanded}
          onToggle={handleToggle}
          ariaControls={`time-motion-settings-${chartId}`}
        />
      </div>

      {isExpanded && (() => {
        // Check if time motion is active (this component's overlay)
        const isThisActive = activeMotionType === 'time-motion';
        // Disabled if planet drag is active
        const isDisabledByOther = activeMotionType === 'planet-drag';

        return (
          <div
            id={`time-motion-settings-${chartId}`}
            className="planet-detail-subsection"
            style={{ opacity: isDisabledByOther ? 0.5 : 1 }}
          >
            <HStack spacing={3} align="center">
              {/* Step selectors */}
              <HStack spacing={1}>
                <Text fontSize="xs" color="gray.500">Step:</Text>
                <StepValueSelector
                  unit={stepUnit}
                  value={stepValue}
                  onChange={handleValueChange}
                  disabled={isDisabledByOther}
                />
                <StepUnitSelector
                  value={stepUnit}
                  onChange={handleUnitChange}
                  disabled={isDisabledByOther}
                />
              </HStack>

              {/* Active badge or Open button */}
              {isThisActive ? (
                <Badge colorScheme="green" variant="solid" fontSize="xs">
                  Active
                </Badge>
              ) : (
                <MotionOpenButton
                  isActive={isOverlayOpen}
                  disabled={disabled || isDisabledByOther}
                  onClick={handleOpenClick}
                />
              )}
            </HStack>

            {/* Range info */}
            <Text fontSize="2xs" color="gray.400" mt={1}>
              Navigate ±{formatRange(stepUnit, stepValue)} from current time
            </Text>
          </div>
        );
      })()}
    </div>
  );
};

export default TimeMotionSettings;
